import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { acceptInvitation, fetchInvitation } from '../../api/users';
import Input from '../../components/ui/Input';
import { useAuth } from '../../contexts/AuthContext';
import { getApiErrorMessage } from '../../utils/errors';
import { setStoredApiKey } from '../../utils/api';
import { userHomePath } from '../../support/accountFunction';

function InviteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, login, user, refreshProfile } = useAuth();

  const [invitation, setInvitation] = useState(null);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const loadInvitation = async () => {
      try {
        const data = await fetchInvitation(token);
        setInvitation(data);
        setLoginEmail(data.email);
      } catch (err) {
        setError(getApiErrorMessage(err, 'Convite inválido ou expirado.'));
      } finally {
        setIsLoading(false);
      }
    };
    loadInvitation();
  }, [token]);

  const acceptForAuthenticatedUser = async () => {
    const result = await acceptInvitation(token, {});
    if (result.api_key) {
      setStoredApiKey(result.api_key);
    }
    await refreshProfile();
    navigate(userHomePath(), { replace: true });
  };

  const handleCreateAndAccept = async (event) => {
    event.preventDefault();
    if (password.length < 8) {
      setError('A senha deve ter pelo menos 8 caracteres.');
      return;
    }
    if (password !== passwordConfirmation) {
      setError('As senhas não coincidem.');
      return;
    }

    setIsSubmitting(true);
    setError('');
    try {
      const result = await acceptInvitation(token, {
        name: name.trim(),
        password,
      });
      if (result.api_key) {
        setStoredApiKey(result.api_key);
      }
      await refreshProfile();
      navigate(userHomePath(), { replace: true });
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível aceitar o convite.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLoginAndAccept = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setError('');
    try {
      await login(loginEmail, loginPassword);
      await acceptForAuthenticatedUser();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível entrar e aceitar o convite.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAcceptWhileLoggedIn = async () => {
    setIsSubmitting(true);
    setError('');
    try {
      await acceptForAuthenticatedUser();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível aceitar o convite.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-slate-200 border-t-forge-600" />
      </div>
    );
  }

  if (!invitation) {
    return (
      <div className="surface-card p-8 shadow-card-md max-w-lg mx-auto text-center">
        <h1 className="text-xl font-semibold text-content-primary mb-2">Convite indisponível</h1>
        <p className="text-content-muted text-sm mb-6">{error || 'Este convite não é mais válido.'}</p>
        <Link to="/login" className="btn-primary inline-flex">
          Ir para login
        </Link>
      </div>
    );
  }

  const loggedInMatchesInvite = isAuthenticated && user?.email === invitation.email;

  return (
    <div className="surface-card p-8 shadow-card-md max-w-lg mx-auto">
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-content-primary mb-2">Convite para workspace</h1>
        <p className="text-content-muted text-sm">
          Você foi convidado para <strong>{invitation.account?.name}</strong> como{' '}
          <strong>{invitation.role}</strong>.
        </p>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 mb-4">
          {error}
        </div>
      )}

      {loggedInMatchesInvite ? (
        <button
          type="button"
          onClick={handleAcceptWhileLoggedIn}
          disabled={isSubmitting}
          className="btn-primary w-full disabled:opacity-50"
        >
          {isSubmitting ? 'Aceitando...' : 'Aceitar convite'}
        </button>
      ) : isAuthenticated ? (
        <div className="space-y-4 text-sm text-content-muted">
          <p>
            Você está logado como <strong>{user?.email}</strong>, mas o convite é para{' '}
            <strong>{invitation.email}</strong>.
          </p>
          <Link to="/login" className="btn-secondary w-full text-center inline-flex justify-center">
            Trocar de conta
          </Link>
        </div>
      ) : (
        <div className="space-y-8">
          <form onSubmit={handleCreateAndAccept} className="space-y-4">
            <h2 className="text-lg font-semibold text-content-primary">Criar senha e entrar</h2>
            <Input
              id="name"
              label="Seu nome"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
            <Input
              id="password"
              label="Senha"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <Input
              id="password_confirmation"
              label="Confirmar senha"
              type="password"
              value={passwordConfirmation}
              onChange={(e) => setPasswordConfirmation(e.target.value)}
              required
            />
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary w-full disabled:opacity-50"
            >
              {isSubmitting ? 'Criando conta...' : 'Aceitar convite'}
            </button>
          </form>

          <form onSubmit={handleLoginAndAccept} className="space-y-4 border-t border-slate-100 pt-6">
            <h2 className="text-lg font-semibold text-content-primary">Já tenho conta</h2>
            <Input
              id="login_email"
              label="E-mail"
              type="email"
              value={loginEmail}
              onChange={(e) => setLoginEmail(e.target.value)}
              required
            />
            <Input
              id="login_password"
              label="Senha"
              type="password"
              value={loginPassword}
              onChange={(e) => setLoginPassword(e.target.value)}
              required
            />
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-secondary w-full disabled:opacity-50"
            >
              Entrar e aceitar
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

export default InviteAccept;
